import React from 'react'
import styled from 'styled-components'
import Visualization from '~/packages/sentinel/visualization/visualization.component'
import insightReadDefinition from '~/packages/insight/read/insightRead.definition'
import useAction from '~/prix/react/hooks/action'
import { getAlertColor, getAlertLabel, getAlertTextColor } from '~/packages/variable/spaceTime.data'
import { normalizeText } from '~/utils/shared/insightText'
import { isUuid } from '~/utils/shared/isUuid'

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
  margin-top: 10px;
`

const InsightCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
`

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  flex: 1;
`

const CardAlert = styled.small<{ $alert: any }>`
  padding: 0.3rem 0.8rem;
  border-radius: 8px;
  align-self: flex-start;
  text-transform: uppercase;
  font-weight: 700;
  background-color: ${({ $alert }) => getAlertColor($alert)};
  color: ${({ $alert }) => getAlertTextColor($alert)};
  white-space: nowrap;
`

const CardMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
`

const CardDescription = styled.div`
  font-size: 12px;
  color: #374151;
  line-height: 1.4;
  margin-bottom: 10px;
`

const LoadingPlaceholder = styled.div`
  height: 60px;
  width: 100%;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.06);
`

function toCleanStringList(value: unknown): string[] {
  if (!value) return []

  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string')
      .map(item => item.trim())
      .filter(Boolean)
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? [trimmed] : []
  }

  return []
}

function buildMetaLine(parts: unknown[]): string {
  const seen = new Set<string>()
  const unique: string[] = []

  for (const part of parts) {
    for (const item of toCleanStringList(part)) {
      const normalized = normalizeText(item)
      if (!normalized) continue
      if (seen.has(normalized)) continue
      seen.add(normalized)
      unique.push(item)
    }
  }

  return unique.join(' • ')
}

function InsightCardFromId({ insightId }: { insightId: string }) {
  if (!isUuid(insightId)) return null

  const insightReadResult = useAction(
    insightReadDefinition,
    { id: insightId },
    [insightId],
    { staleWhileRevalidate: true },
  )

  if (insightReadResult.isLoading && !insightReadResult.result) {
    return (
      <InsightCard>
        <LoadingPlaceholder />
      </InsightCard>
    )
  }

  if (insightReadResult.error) {
    return (
      <InsightCard>
        Não foi possível carregar este insight: {insightReadResult.error.message}
      </InsightCard>
    )
  }

  const insight = (insightReadResult.result as any)?.insight
  if (!insight) {
    return null
  }

  const visualizations = Array.isArray(insight.visualizations) ? insight.visualizations : []

  const meta = buildMetaLine([
    insight.delimitationTitle,
    insight.variableTitle,
    insight.variableLabel,
  ])

  return (
    <InsightCard>
      <CardHeader>
        <CardTitle>{insight.title}</CardTitle>
        {insight.alert ? (
          <CardAlert $alert={insight.alert}>{getAlertLabel(insight.alert)}</CardAlert>
        ) : null}
      </CardHeader>

      {meta ? <CardMeta>{meta}</CardMeta> : null}
      {insight.description ? <CardDescription>{insight.description}</CardDescription> : null}

      {visualizations.length === 0 ? (
        <CardMeta>Sem visualizações disponíveis para este insight.</CardMeta>
      ) : null}

      {visualizations.map((visualization: any, visualizationIndex: number) => (
        <Visualization
          key={visualizationIndex}
          visualization={visualization}
          isExpanded={false}
          insightTitle={insight.title}
          alert={insight.alert ?? null}
        />
      ))}
    </InsightCard>
  )
}

export default function ShowInsightCards({ insightIds }: { insightIds: string[] }) {
  const safeInsightIds = Array.from(new Set(insightIds.filter(isUuid)))
  if (!safeInsightIds.length) return null

  return (
    <CardsGrid>
      {safeInsightIds.map(insightId => (
        <InsightCardFromId key={insightId} insightId={insightId} />
      ))}
    </CardsGrid>
  )
}
